import {
  Checkbox as MuiCheckbox,
  CheckboxProps as MuiCheckboxProps,
  FormControlLabel,
  Typography,
} from '@mui/material';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import Icon from '../../IconSvg/Icon';
import { LelyIconName } from '../buttonTypes';

export type LelyMultipleToggleCheckboxState = {
  value: string;
  testId: string;
  icon?: LelyIconName;
  ariaLabel?: string;
};

export type LelyMultipleToggleCheckboxProps = Omit<
  MuiCheckboxProps,
  | 'checked'
  | 'checkedIcon'
  | 'children'
  | 'icon'
  | 'indeterminateIcon'
  | 'onChange'
  | 'value'
> & {
  'data-testid'?: string;
  children?: ReactNode;
  defaultValue?: string;
  dense?: boolean;
  label?: ReactNode;
  onChange?: (
    value: string,
    state: LelyMultipleToggleCheckboxState,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  states: LelyMultipleToggleCheckboxState[];
  value: string;
};

function LelyMultipleToggleCheckbox({
  children,
  defaultValue,
  dense,
  label,
  onChange,
  slotProps,
  states = [],
  value,
  'data-testid': dataTestId,
  ...props
}: LelyMultipleToggleCheckboxProps) {
  const initialValue = defaultValue ?? states[0]?.value ?? '';
  const [uncontrolledValue, setUncontrolledValue] = useState(initialValue);
  const selectedValue = value ?? uncontrolledValue;

  useEffect(() => {
    if (value === undefined) {
      setUncontrolledValue(initialValue);
    }
  }, [initialValue, value]);

  const selectedIndex = useMemo(() => {
    const stateIndex = states.findIndex((state) => state.value === selectedValue);
    return stateIndex >= 0 ? stateIndex : 0;
  }, [selectedValue, states]);
  const selectedState = states[selectedIndex] ?? states[0];
  const nextState = states[(selectedIndex + 1) % states.length] ?? selectedState;

  const checkbox = (
    <MuiCheckbox
      {...props}
      checked={Boolean(selectedState?.icon)}
      checkedIcon={
        selectedState?.icon ? <Icon name={selectedState.icon} /> : <span />
      }
      className={
        props.className
          ? `${props.className} multiple-toggle-checkbox`
          : 'multiple-toggle-checkbox'
      }
      data-dense={dense || undefined}
      data-testid={dataTestId ?? selectedState?.testId}
      icon={<span />}
      slotProps={{
        ...slotProps,
        input: {
          'aria-label': selectedState?.ariaLabel,
          ...slotProps?.input,
        },
      }}
      onChange={(event) => {
        if (value === undefined) {
          setUncontrolledValue(nextState.value);
        }

        onChange?.(nextState.value, nextState, event);
      }}
    />
  );

  const labelContent = label ?? children;

  if (labelContent) {
    return (
      <FormControlLabel
        control={checkbox}
        data-testid="multiple-toggle-checkbox-form-control"
        label={
          <Typography variant="xs" component="span" data-testid="multiple-toggle-checkbox-label">
            {labelContent}
          </Typography>
        }
      />
    );
  }

  return checkbox;
}

export default LelyMultipleToggleCheckbox;